'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useLanguage } from '@/contexts/LanguageContext';

interface CardFeature {
  text: string;
  textEn: string;
  strikethrough?: boolean;
}

interface CardData {
  id: string;
  title: string;
  titleEn: string;
  subtitle: string;
  subtitleEn: string;
  price: string;
  priceEn: string;
  duration: string;
  durationEn: string;
  headerGradient: string;
  bgGradient: string;
  borderColor: string;
  textColor: string;
  subtitleColor: string;
  buttonGradient: string;
  image?: string;
  features: CardFeature[];
}

type AnimationState = 'idle' | 'animating';

const CardsComparisonSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [animState, setAnimState] = useState<AnimationState>('idle');
  // leftCard and rightCard track what's visible; nextCard is the incoming one
  const [leftCardIndex, setLeftCardIndex] = useState(0);
  const [rightCardIndex, setRightCardIndex] = useState(1);
  const [incomingCardIndex, setIncomingCardIndex] = useState(2);
  // animation classes
  const [leftClass, setLeftClass] = useState('');
  const [rightClass, setRightClass] = useState('');
  const [incomingClass, setIncomingClass] = useState('');
  const animatingRef = useRef(false);
  const { t } = useLanguage();

  const cards: CardData[] = [
    {
      id: 'personal',
      title: 'جودة الحياة شخصي',
      titleEn: 'Quality of Life Personal',
      subtitle: 'للأفراد - اشتراك سنوي',
      subtitleEn: 'For Individuals - Annual Subscription',
      price: '150 ريال',
      priceEn: '150 SAR',
      duration: 'سنوياً',
      durationEn: 'Annually',
      headerGradient: 'bg-gradient-to-r from-emerald-600 to-teal-600',
      bgGradient: 'bg-gradient-to-br from-emerald-50 to-teal-100',
      borderColor: 'border-emerald-300',
      textColor: 'text-emerald-900',
      subtitleColor: 'text-emerald-100',
      buttonGradient: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700',
      image: '/assets/images/WhatsApp_Image_2026-03-29_at_8.21.55_PM-1774805462574.jpeg',
      features: [
        { text: 'خصومات على المطاعم', textEn: 'Restaurant discounts' },
        { text: 'خصومات على العيادات الطبية', textEn: 'Medical clinic discounts' },
        { text: 'خصومات على الملابس', textEn: 'Clothing discounts' },
        { text: 'خصومات على الحلويات', textEn: 'Sweets discounts' },
        { text: 'خصومات على النوادي الرياضية', textEn: 'Sports club discounts' },
        { text: 'تغطية حي 6 أفراد', textEn: 'Coverage for 6 members', strikethrough: true },
        { text: 'خصومات أعلى على جميع الفئات', textEn: 'Higher discounts on all categories', strikethrough: true },
        { text: 'عروض حصرية شهرية', textEn: 'Monthly exclusive offers' },
        { text: 'أولوية في الحجوزات', textEn: 'Priority bookings', strikethrough: true },
        { text: 'دعم فني على مدار الساعة', textEn: '24/7 technical support' },
      ],
    },
    {
      id: 'family3month',
      title: 'جودة الحياة عائلي ربع سنوي',
      titleEn: 'Quality of Life Family Quarterly',
      subtitle: 'للعائلات - تجربة 3 أشهر',
      subtitleEn: 'For Families - 3 Month Trial',
      price: '250 ريال',
      priceEn: '250 SAR',
      duration: '3 أشهر فقط',
      durationEn: '3 Months Only',
      headerGradient: 'bg-gradient-to-r from-blue-600 to-cyan-600',
      bgGradient: 'bg-gradient-to-br from-blue-50 to-cyan-100',
      borderColor: 'border-blue-300',
      textColor: 'text-blue-900',
      subtitleColor: 'text-blue-100',
      buttonGradient: 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700',
      features: [
        { text: 'خصومات على المطاعم', textEn: 'Restaurant discounts' },
        { text: 'خصومات على العيادات الطبية', textEn: 'Medical clinic discounts' },
        { text: 'خصومات على الملابس', textEn: 'Clothing discounts' },
        { text: 'خصومات على الحلويات', textEn: 'Sweets discounts' },
        { text: 'خصومات على النوادي الرياضية', textEn: 'Sports club discounts' },
        { text: 'تغطية حي 6 أفراد', textEn: 'Coverage for 6 members' },
        { text: 'خصومات أعلى على جميع الفئات', textEn: 'Higher discounts on all categories', strikethrough: true },
        { text: 'عروض حصرية شهرية', textEn: 'Monthly exclusive offers' },
        { text: 'أولوية في الحجوزات', textEn: 'Priority bookings' },
        { text: 'دعم فني على مدار الساعة', textEn: '24/7 technical support' },
      ],
    },
    {
      id: 'vip',
      title: 'جودة الحياة VIP',
      titleEn: 'Quality of Life VIP',
      subtitle: 'للأفراد والعائلات المميزة',
      subtitleEn: 'For Premium Individuals & Families',
      price: '1500 ريال',
      priceEn: '1500 SAR',
      duration: 'سنوياً',
      durationEn: 'Annually',
      headerGradient: 'bg-gradient-to-r from-gray-800 to-gray-900',
      bgGradient: 'bg-white',
      borderColor: 'border-gray-200',
      textColor: 'text-gray-800',
      subtitleColor: 'text-gray-300',
      buttonGradient: 'bg-gray-900 hover:bg-gray-800',
      features: [
        { text: 'خصومات على المطاعم', textEn: 'Restaurant discounts' },
        { text: 'خصومات على العيادات الطبية', textEn: 'Medical clinic discounts' },
        { text: 'خصومات على الملابس', textEn: 'Clothing discounts' },
        { text: 'خصومات على الحلويات', textEn: 'Sweets discounts' },
        { text: 'خصومات على النوادي الرياضية', textEn: 'Sports club discounts' },
        { text: 'تغطية حي 6 أفراد', textEn: 'Coverage for 6 members' },
        { text: 'خصومات أعلى على جميع الفئات', textEn: 'Higher discounts on all categories' },
        { text: 'عروض حصرية شهرية', textEn: 'Monthly exclusive offers' },
        { text: 'أولوية في الحجوزات', textEn: 'Priority bookings' },
        { text: 'دعم فني على مدار الساعة', textEn: '24/7 technical support' },
      ],
    },
    {
      id: 'family',
      title: 'جودة الحياة عائلي',
      titleEn: 'Quality of Life Family',
      subtitle: 'للعائلات الباحثة عن التوفير',
      subtitleEn: 'For Savings-Seeking Families',
      price: '800 ريال',
      priceEn: '800 SAR',
      duration: 'سنوياً',
      durationEn: 'Annually',
      headerGradient: 'bg-gradient-to-r from-amber-600 to-yellow-600',
      bgGradient: 'bg-gradient-to-br from-amber-50 to-yellow-100',
      borderColor: 'border-amber-300',
      textColor: 'text-amber-900',
      subtitleColor: 'text-amber-100',
      buttonGradient: 'bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700',
      features: [
        { text: 'خصومات على المطاعم', textEn: 'Restaurant discounts' },
        { text: 'خصومات على العيادات الطبية', textEn: 'Medical clinic discounts' },
        { text: 'خصومات على الملابس', textEn: 'Clothing discounts' },
        { text: 'خصومات على الحلويات', textEn: 'Sweets discounts' },
        { text: 'خصومات على النوادي الرياضية', textEn: 'Sports club discounts' },
        { text: 'تغطية حي 6 أفراد', textEn: 'Coverage for 6 members' },
        { text: 'خصومات أعلى على جميع الفئات', textEn: 'Higher discounts on all categories', strikethrough: true },
        { text: 'عروض حصرية شهرية', textEn: 'Monthly exclusive offers' },
        { text: 'أولوية في الحجوزات', textEn: 'Priority bookings' },
        { text: 'دعم فني على مدار الساعة', textEn: '24/7 technical support' },
      ],
    },
    {
      id: 'corporate',
      title: 'جودة الحياة شركات',
      titleEn: 'Quality of Life Corporate',
      subtitle: 'للشركات - عدد مرن من الموظفين',
      subtitleEn: 'For Companies - Flexible Number of Employees',
      price: '150 ريال / فرد',
      priceEn: '150 SAR / person',
      duration: 'سنوياً',
      durationEn: 'Annually',
      headerGradient: 'bg-gradient-to-r from-indigo-700 to-purple-700',
      bgGradient: 'bg-gradient-to-br from-indigo-50 to-purple-100',
      borderColor: 'border-indigo-300',
      textColor: 'text-indigo-900',
      subtitleColor: 'text-indigo-100',
      buttonGradient: 'bg-gradient-to-r from-indigo-700 to-purple-700 hover:from-indigo-800 hover:to-purple-800',
      image: 'https://ocadeynpyrbhhpnmlizj.supabase.co/storage/v1/object/sign/123/WhatsApp%20Image%202026-04-25%20at%2012.38.41%20PM.jpeg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV82ZDJlYmU2My03ZDcwLTQ1ODYtOWNhMC1mMWM2NjNiZjZkZDUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiIxMjMvV2hhdHNBcHAgSW1hZ2UgMjAyNi0wNC0yNSBhdCAxMi4zOC40MSBQTS5qcGVnIiwiaWF0IjoxNzc3MTA5OTYxLCJleHAiOjE4MDg2NDU5NjF9.cWo1_g8SuflpVV_jnW7wLBIKZLOJV8_EGRhZyMvNx9E',
      features: [
        { text: 'خصومات على المطاعم', textEn: 'Restaurant discounts' },
        { text: 'خصومات على العيادات الطبية', textEn: 'Medical clinic discounts' },
        { text: 'خصومات على الملابس', textEn: 'Clothing discounts' },
        { text: 'خصومات على الحلويات', textEn: 'Sweets discounts' },
        { text: 'خصومات على النوادي الرياضية', textEn: 'Sports club discounts' },
        { text: 'عدد مرن من الموظفين', textEn: 'Flexible number of employees' },
        { text: 'عروض حصرية شهرية', textEn: 'Monthly exclusive offers' },
        { text: 'أولوية في الحجوزات', textEn: 'Priority bookings' },
        { text: 'دعم فني على مدار الساعة', textEn: '24/7 technical support' },
      ],
    },
  ];

  // ─── Desktop single-card carousel ───────────────────────────────────────────
  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % cards.length);
  }, [cards.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
  }, [cards.length]);

  const handleArrowClick = (direction: 'prev' | 'next') => {
    setIsAutoPlaying(false);
    if (direction === 'next') goNext();
    else goPrev();
    // Resume auto-play after 7 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 7000);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(goNext, 7000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, goNext]);

  const card = cards[currentIndex];

  // ─── Mobile two-card sliding animation ──────────────────────────────────────
  const triggerMobileSlide = useCallback(() => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    setAnimState('animating');

    // Step 1: start animation — left fades out, right slides left, incoming slides in from right
    setLeftClass('mobile-card-exit-left');
    setRightClass('mobile-card-slide-left');
    setIncomingClass('mobile-card-enter-right');

    // Step 2: after animation ends, update indices and reset
    setTimeout(() => {
      setLeftCardIndex((prev) => (prev + 1) % cards.length);
      setRightCardIndex((prev) => (prev + 1) % cards.length);
      setIncomingCardIndex((prev) => (prev + 1) % cards.length);
      setLeftClass('');
      setRightClass('');
      setIncomingClass('');
      setAnimState('idle');
      animatingRef.current = false;
    }, 600);
  }, [cards.length]);

  useEffect(() => {
    const interval = setInterval(triggerMobileSlide, 7000);
    return () => clearInterval(interval);
  }, [triggerMobileSlide]);

  const renderMiniCard = (cardData: CardData, extraClass: string, key: string) => (
    <div
      key={key}
      className={`mobile-card flex-shrink-0 w-[calc(50%-6px)] ${cardData.bgGradient} rounded-xl shadow-md overflow-hidden border ${cardData.borderColor} ${extraClass}`}
    >
      <div className={`${cardData.headerGradient} text-white p-2 text-center`}>
        <h3 className="text-xs font-bold leading-tight line-clamp-2">{t(cardData.title, cardData.titleEn)}</h3>
        <p className={`${cardData.subtitleColor} text-[10px] mt-0.5 line-clamp-1`}>{t(cardData.subtitle, cardData.subtitleEn)}</p>
      </div>
      <div className="p-3">
        <div className="text-center mb-2">
          <div className={`text-lg font-bold ${cardData.textColor}`}>{t(cardData.price, cardData.priceEn)}</div>
          <div className={`${cardData.textColor} opacity-70 text-[10px]`}>{t(cardData.duration, cardData.durationEn)}</div>
        </div>
        <ul className="space-y-1 mb-3">
          {cardData.features.slice(0, 5).map((feature, index) => (
            <li key={index} className="flex items-start gap-1">
              <CheckCircleIcon className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
              <span className={`${cardData.textColor} text-[10px] leading-tight ${feature.strikethrough ? 'line-through opacity-50' : ''}`}>
                {t(feature.text, feature.textEn)}
              </span>
            </li>
          ))}
        </ul>
        <Link href="/subscription-registration" className="block w-full">
          <button className={`w-full ${cardData.buttonGradient} text-white font-bold py-1.5 px-2 rounded-lg transition-all duration-300 text-[10px] shadow`}>
            {t('اشترك الآن', 'Subscribe Now')}
          </button>
        </Link>
      </div>
    </div>
  );

  return (
    <section className="py-16 px-4 bg-secondary border-t border-accent/20">
      <div className="max-w-2xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl lg:text-4xl font-bold text-white font-amiri mb-3">
            {t('بطاقات الاشتراك', 'Subscription Cards')}
          </h2>
          <p className="text-white/70 text-sm lg:text-base">
            {t('اختر البطاقة المناسبة لاحتياجاتك', 'Choose the card that suits your needs')}
          </p>
        </div>

        {/* ── MOBILE VIEW (< md) ── */}
        <div className="block md:hidden">
          <div className="mobile-carousel-wrapper overflow-hidden">
            <div className={`mobile-carousel-track flex gap-3 ${animState === 'animating' ? 'pointer-events-none' : ''}`}>
              {renderMiniCard(cards[leftCardIndex], leftClass, `left-${leftCardIndex}`)}
              {renderMiniCard(cards[rightCardIndex], rightClass, `right-${rightCardIndex}`)}
              {animState === 'animating' && renderMiniCard(cards[incomingCardIndex], incomingClass, `incoming-${incomingCardIndex}`)}
            </div>
          </div>
          {/* Dots for mobile */}
          <div className="flex justify-center gap-1.5 mt-4">
            {cards.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === leftCardIndex || index === rightCardIndex
                    ? 'bg-accent w-4' : 'bg-white/30 w-1.5'
                }`}
              />
            ))}
          </div>
        </div>

        {/* ── DESKTOP VIEW (≥ md) ── */}
        <div className="hidden md:block">
          <div className="relative flex items-center gap-4">
            {/* Left Arrow */}
            <button
              onClick={() => handleArrowClick('prev')}
              className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 border border-accent/30 flex items-center justify-center hover:bg-accent/20 transition-all duration-200 hover:shadow-xl z-10"
              aria-label={t('السابق', 'Previous')}
            >
              <ChevronRightIcon className="w-6 h-6 text-white" />
            </button>

            {/* Card */}
            <div
              key={card.id}
              className={`flex-1 ${card.bgGradient} rounded-2xl shadow-xl overflow-hidden border-2 ${card.borderColor} transition-all duration-500`}
            >
              <div className={`${card.headerGradient} text-white p-6 text-center`}>
                <h3 className="text-3xl font-bold mb-2">{t(card.title, card.titleEn)}</h3>
                <p className={`${card.subtitleColor} text-lg`}>{t(card.subtitle, card.subtitleEn)}</p>
              </div>

              <div className="p-8">
                <div className="text-center mb-8">
                  <div className={`text-5xl font-bold ${card.textColor} mb-2`}>{t(card.price, card.priceEn)}</div>
                  <div className={`${card.textColor} opacity-70 text-lg`}>{t(card.duration, card.durationEn)}</div>
                </div>

                <ul className="space-y-3 mb-8">
                  {card.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className={`${card.textColor} text-lg ${feature.strikethrough ? 'line-through opacity-50' : ''}`}>
                        {t(feature.text, feature.textEn)}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link href="/subscription-registration" className="block w-full">
                  <button className={`w-full ${card.buttonGradient} text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 text-lg shadow-lg`}>
                    {t('اشترك الآن', 'Subscribe Now')}
                  </button>
                </Link>
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => handleArrowClick('next')}
              className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 border border-accent/30 flex items-center justify-center hover:bg-accent/20 transition-all duration-200 hover:shadow-xl z-10"
              aria-label={t('التالي', 'Next')}
            >
              <ChevronLeftIcon className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {cards.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsAutoPlaying(false);
                  setTimeout(() => setIsAutoPlaying(true), 7000);
                }}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  currentIndex === index ? 'bg-accent w-6' : 'bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`${t('الانتقال إلى البطاقة', 'Go to card')} ${index + 1}`}
              />
            ))}
          </div>
        </div>

      </div>

      {/* Animation styles */}
      <style jsx>{`
        .mobile-carousel-wrapper {
          position: relative;
          width: 100%;
        }
        .mobile-carousel-track {
          position: relative;
          width: 100%;
        }
        .mobile-card {
          transition: transform 0.6s ease, opacity 0.6s ease;
        }
        .mobile-card-exit-left {
          animation: exitLeft 0.6s ease forwards;
        }
        .mobile-card-slide-left {
          animation: slideLeft 0.6s ease forwards;
        }
        .mobile-card-enter-right {
          animation: enterRight 0.6s ease forwards;
        }
        @keyframes exitLeft {
          from { opacity: 1; transform: translateX(0); }
          to   { opacity: 0; transform: translateX(-30px); }
        }
        @keyframes slideLeft {
          from { transform: translateX(0); }
          to   { transform: translateX(calc(-100% - 12px)); }
        }
        @keyframes enterRight {
          from { opacity: 0; transform: translateX(30px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </section>
  );
};

export default CardsComparisonSection;